/**
 * Voyado Elevate search — drawer autocomplete and search page.
 */

const { ElevateCore } = window;

function debounce(fn, delay) {
  let timeoutId;

  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => fn(...args), delay);
  };
}

class ElevatePredictiveSearch extends HTMLElement {
  #config = null;
  #fetchAbortController = null;
  #searchForm = null;
  #queryInput = null;

  connectedCallback() {
    this.#config = ElevateCore.readConfigFromElement(this);

    if (!this.#config?.clusterId || !this.#config.market || !this.#config.locale) {
      return;
    }

    this.attachShadow({ mode: 'open' });
    this.shadowRoot.appendChild(document.createRange().createContextualFragment('<slot name="placeholder"></slot>'));

    this.#searchForm = document.querySelector(`[aria-owns="${this.id}"]`);
    this.#queryInput = this.#searchForm?.elements?.q;

    if (!this.#searchForm || !this.#queryInput) {
      return;
    }

    this.#searchForm.addEventListener('submit', this.#onFormSubmitted.bind(this));
    this.#searchForm.addEventListener('reset', this.#onSearchCleared.bind(this));
    this.#queryInput.addEventListener(
      'input',
      debounce(this.#onInputChanged.bind(this), parseInt(this.dataset.autocompleteDelay || '280', 10))
    );
  }

  #searchUrl() {
    return this.#searchForm.action || `${Shopify.routes.root}search`;
  }

  #onFormSubmitted(event) {
    if (this.#queryInput.value === '') {
      event.preventDefault();
    }
  }

  #onSearchCleared() {
    this.#fetchAbortController?.abort();
    this.#queryInput.focus();
    this.shadowRoot.innerHTML = '<slot name="placeholder"></slot>';
  }

  async #onInputChanged() {
    if (this.#queryInput.value === '') {
      return this.#onSearchCleared();
    }

    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const data = await ElevateCore.fetchAutocomplete(this.#config, {
        q: this.#queryInput.value,
        signal: this.#fetchAbortController.signal,
        priority: 'low'
      });

      const result = ElevateCore.extractAutocompleteProducts(data);
      const sectionNode = await ElevateCore.renderProductsSection(
        this.#config,
        result.productIds,
        this.#config.resultsSectionId
      );

      this.shadowRoot.innerHTML = '<slot name="results"></slot>';
      const resultsSlot = this.querySelector('[slot="results"]');
      const wrapper = document.createElement('div');

      const phraseSuggestions = ElevateCore.buildPhraseSuggestionLinks(result.phraseSuggestions, this.#searchUrl());
      const contentMarkup = ElevateCore.buildContentMarkup(
        result.contentLists,
        this.dataset.contentTitle || ''
      );

      if (phraseSuggestions) {
        const suggestionsList = document.createElement('ul');
        suggestionsList.className = 'predictive-search__suggestions unstyled-list scroll-area bleed';
        suggestionsList.innerHTML = phraseSuggestions;
        wrapper.appendChild(suggestionsList);
      }

      if (contentMarkup) {
        const contentWrap = document.createElement('div');
        contentWrap.className = 'predictive-search__content v-stack gap-4';
        contentWrap.innerHTML = contentMarkup;
        wrapper.appendChild(contentWrap);
      }

      if (sectionNode && result.productIds.length > 0) {
        const productsWrapper = document.createElement('div');
        productsWrapper.innerHTML = sectionNode.innerHTML;
        wrapper.append(...productsWrapper.childNodes);
        ElevateCore.attachTickets(wrapper, result.tickets, result.productIds);
      } else if (!phraseSuggestions && !contentMarkup) {
        const recentSuggestions = ElevateCore.buildRecentSearchLinks(result.recentSearches, this.#searchUrl());

        if (recentSuggestions) {
          const recentList = document.createElement('ul');
          recentList.className = 'predictive-search__suggestions unstyled-list scroll-area bleed';
          recentList.innerHTML = recentSuggestions;
          wrapper.appendChild(recentList);
        }

        const noResults = document.createElement('div');
        noResults.className = 'predictive-search__no-results prose prose-justify-center';
        noResults.innerHTML = `
          <p class="subheading">${ElevateCore.escapeHtml(this.dataset.noResultsTitle || 'Search')}</p>
          <p class="h5">${ElevateCore.escapeHtml(this.dataset.noResultsText || 'No results could be found.')}</p>
        `;
        wrapper.appendChild(noResults);
      } else {
        const noProducts = document.createElement('div');
        noProducts.className = 'predictive-search__no-results prose prose-justify-center';
        noProducts.innerHTML = `<p class="h5">${ElevateCore.escapeHtml(this.dataset.noResultsText || 'No results could be found.')}</p>`;
        wrapper.appendChild(noProducts);
      }

      resultsSlot.replaceChildren(...wrapper.childNodes);
      ElevateCore.bindClickTracking(resultsSlot, this.#config);
    } catch (error) {
      if (error.name !== 'AbortError') {
        throw error;
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }
}

class ElevateSearchPage extends HTMLElement {
  #config = null;

  connectedCallback() {
    this.#config = ElevateCore.readConfigFromElement(this);

    if (!this.#config?.clusterId || !this.#config.market || !this.#config.locale) {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const query = urlParams.get('q')?.trim();

    if (!query) {
      return;
    }

    this.loadResults(query);
  }

  async loadResults(inputQuery) {
    const urlParams = new URLSearchParams(window.location.search);
    const page = Math.max(parseInt(urlParams.get('page') || '1', 10), 1);
    const sort = urlParams.get('sort') || '';
    const origin = urlParams.get('origin') || 'ORGANIC';
    const skip = (page - 1) * this.#config.productsPerPage;
    const loadKey = [inputQuery, page, sort, origin, skip].join('|');

    if (this.dataset.loadedKey === loadKey) {
      return;
    }

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const data = await ElevateCore.fetchSearchPage(this.#config, {
        q: inputQuery,
        limit: this.#config.productsPerPage,
        skip,
        sort: sort || undefined,
        origin
      });

      const result = ElevateCore.extractPrimaryListProducts(data);
      const secondary = ElevateCore.extractSecondaryListProducts(data);
      const effectiveQuery = result.effectiveQuery || inputQuery;
      const resultsContainer = this.querySelector('[data-elevate-search-results]');
      const countContainer = this.querySelector('[data-elevate-search-count]');
      const metaContainer = this.querySelector('[data-elevate-search-meta]');
      const paginationContainer = this.querySelector('[data-elevate-search-pagination]');
      const secondaryContainer = this.querySelector('[data-elevate-search-secondary]');
      const secondaryTitle = this.querySelector('[data-elevate-secondary-title]');
      const secondaryResults = this.querySelector('[data-elevate-search-secondary-results]');

      if (countContainer) {
        const template = result.totalHits === 1 ? countContainer.dataset.countOne : countContainer.dataset.countOther;
        countContainer.textContent = (template || '{{ count }} results').replace('__COUNT__', String(result.totalHits));
      }

      if (metaContainer) {
        metaContainer.innerHTML = ElevateCore.buildSearchMetaMarkup({
          autoCorrect: result.autoCorrect,
          didYouMean: result.didYouMean,
          q: inputQuery,
          searchUrl: `${Shopify.routes.root}search`
        });
      }

      const contentContainer = this.querySelector('[data-elevate-search-content]');
      const contentMarkup = ElevateCore.buildContentMarkup(
        result.contentLists,
        this.dataset.contentTitle || ''
      );

      if (contentContainer) {
        if (contentMarkup) {
          contentContainer.innerHTML = contentMarkup;
          contentContainer.hidden = false;
        } else {
          contentContainer.innerHTML = '';
          contentContainer.hidden = true;
        }
      }

      if (!resultsContainer) {
        return;
      }

      if (!result.productIds.length) {
        resultsContainer.innerHTML = `
          <div class="empty-state empty-state--sm">
            <div class="prose prose-justify-center prose--compact">
              <p>${ElevateCore.escapeHtml(this.dataset.noResultsText || 'No results could be found.')}</p>
            </div>
          </div>
        `;

        if (paginationContainer) {
          paginationContainer.innerHTML = '';
        }

        if (secondaryContainer) {
          secondaryContainer.hidden = true;
          secondaryResults.innerHTML = '';
        }
      } else {
        const sectionNode = await ElevateCore.renderProductsSection(
          this.#config,
          result.productIds,
          this.#config.resultsSectionId
        );

        if (!sectionNode) {
          return;
        }

        resultsContainer.innerHTML = sectionNode.innerHTML;
        ElevateCore.attachTickets(resultsContainer, result.tickets, result.productIds);
        ElevateCore.bindClickTracking(resultsContainer, this.#config);

        if (paginationContainer) {
          paginationContainer.innerHTML = this.#buildPagination({
            effectiveQuery,
            page,
            totalHits: result.totalHits,
            sort,
            origin
          });
        }
      }

      if (secondaryContainer && secondaryResults) {
        if (secondary.productIds.length) {
          const secondarySectionNode = await ElevateCore.renderProductsSection(
            this.#config,
            secondary.productIds,
            this.#config.secondaryResultsSectionId
          );

          if (secondarySectionNode) {
            secondaryTitle.textContent = secondary.title || this.dataset.secondaryTitle || 'Related results';
            secondaryResults.innerHTML = secondarySectionNode.innerHTML;
            ElevateCore.attachTickets(secondaryResults, secondary.tickets, secondary.productIds);
            ElevateCore.bindClickTracking(secondaryResults, this.#config);
            secondaryContainer.hidden = false;
          } else {
            secondaryContainer.hidden = true;
            secondaryResults.innerHTML = '';
          }
        } else {
          secondaryContainer.hidden = true;
          secondaryResults.innerHTML = '';
        }
      }

      this.closest('.shopify-section')?.querySelector('.empty-state')?.remove();
      this.hidden = false;
      this.dataset.loadedKey = loadKey;

      if (effectiveQuery !== inputQuery && page === 1 && !urlParams.get('origin')) {
        const url = new URL(window.location.href);
        url.searchParams.set('q', effectiveQuery);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);

        const searchInput = this.querySelector('input[name="q"]');

        if (searchInput) {
          searchInput.value = effectiveQuery;
        }
      }
    } finally {
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  #buildPagination({ effectiveQuery, page, totalHits, sort, origin }) {
    const totalPages = Math.max(Math.ceil(totalHits / this.#config.productsPerPage), 1);

    if (totalPages <= 1) {
      return '';
    }

    const buildUrl = (targetPage) => {
      const url = new URL(window.location.href);
      url.searchParams.set('q', effectiveQuery);
      url.searchParams.set('page', String(targetPage));

      if (sort) {
        url.searchParams.set('sort', sort);
      } else {
        url.searchParams.delete('sort');
      }

      if (origin && origin !== 'ORGANIC') {
        url.searchParams.set('origin', origin);
      } else {
        url.searchParams.delete('origin');
      }

      url.hash = 'collection';
      return `${url.pathname}${url.search}${url.hash}`;
    };

    const previousDisabled = page <= 1;
    const nextDisabled = page >= totalPages;
    const prevControl = previousDisabled
      ? '<span class="circle-button circle-button--lg" aria-disabled="true">‹</span>'
      : `<a class="circle-button circle-button--lg" rel="prev" href="${buildUrl(page - 1)}" aria-label="Previous page">‹</a>`;
    const nextControl = nextDisabled
      ? '<span class="circle-button circle-button--lg" aria-disabled="true">›</span>'
      : `<a class="circle-button circle-button--lg" rel="next" href="${buildUrl(page + 1)}" aria-label="Next page">›</a>`;

    return `
      <nav class="pagination" role="navigation" aria-label="Pagination">
        ${prevControl}
        <span class="pagination__link subheading" aria-current="page">${page} / ${totalPages}</span>
        ${nextControl}
      </nav>
    `;
  }
}

if (!window.customElements.get('elevate-predictive-search')) {
  window.customElements.define('elevate-predictive-search', ElevatePredictiveSearch);
}

if (!window.customElements.get('elevate-search-page')) {
  window.customElements.define('elevate-search-page', ElevateSearchPage);
}
